<template>
    <div
        class="audit-agent-response"
        @click="handleCitationClick"
        @mouseover="handleCitationHover"
    >
        <template v-for="(segment, index) in renderedSegments" :key="index">
            <AuditCodeBlock
                v-if="segment.type === 'code'"
                :code="segment.code"
                :language="segment.language"
            />
            <div
                v-else
                class="audit-agent-markdown"
                v-html="segment.html"
            ></div>
        </template>
    </div>
</template>

<script>
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import AuditCodeBlock from './AuditCodeBlock.vue';
import { renderAuditCitationMarkers } from './auditAgent.js';

export default {
    name: 'AuditAgentResponse',
    components: { AuditCodeBlock },
    props: {
        content: { type: String, default: '' },
        references: { type: Array, default: () => [] },
    },
    emits: ['open-reference'],
    computed: {
        renderedSegments() {
            let tokens;
            try {
                tokens = marked.lexer(this.content, { gfm: true });
            } catch {
                tokens = [{ type: 'text', raw: this.content }];
            }
            const segments = [];
            let markdown = '';
            const flushMarkdown = () => {
                if (!markdown) return;
                segments.push({
                    type: 'markdown',
                    html: this.renderMarkdown(markdown),
                });
                markdown = '';
            };
            tokens.forEach((token) => {
                if (token.type === 'code') {
                    flushMarkdown();
                    segments.push({
                        type: 'code',
                        code: token.text || '',
                        language: token.lang || '',
                    });
                } else {
                    markdown += token.raw || '';
                }
            });
            flushMarkdown();
            return segments;
        },
    },
    methods: {
        renderMarkdown(content) {
            const renderer = new marked.Renderer();
            renderer.html = () => '';
            const html = marked.parse(content, {
                breaks: false,
                gfm: true,
                renderer,
            });
            const sanitized = DOMPurify.sanitize(html, {
                ALLOWED_TAGS: [
                    'a',
                    'blockquote',
                    'br',
                    'code',
                    'del',
                    'em',
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'hr',
                    'li',
                    'ol',
                    'p',
                    'pre',
                    'strong',
                    'table',
                    'tbody',
                    'td',
                    'th',
                    'thead',
                    'tr',
                    'ul',
                ],
                ALLOWED_ATTR: [
                    'aria-label',
                    'class',
                    'data-citation-id',
                    'href',
                    'rel',
                    'scope',
                    'target',
                    'title',
                    'type',
                ],
            });
            const scrollableTables = sanitized
                .replace(
                    /<table(\s[^>]*)?>/g,
                    '<div class="audit-table-scroll" role="region" aria-label="Scrollable audit results" tabindex="0"><table$1>'
                )
                .replace(/<\/table>/g, '</table></div>');
            return renderAuditCitationMarkers(
                scrollableTables,
                this.references
            );
        },
        citationTarget(event) {
            const target = event.target?.closest?.(
                '.audit-inline-citation[data-citation-id]'
            );
            if (!target || !event.currentTarget?.contains(target)) return null;
            return target;
        },
        citationReference(target) {
            const citationId = target?.getAttribute('data-citation-id');
            return this.references.find(
                (reference) => reference.citationId === citationId
            );
        },
        handleCitationClick(event) {
            const target = this.citationTarget(event);
            const reference = this.citationReference(target);
            if (reference) this.$emit('open-reference', reference);
        },
        handleCitationHover(event) {
            const target = this.citationTarget(event);
            const reference = this.citationReference(target);
            if (target && reference?.label) target.title = reference.label;
        },
    },
};
</script>

<style scoped>
.audit-agent-response {
    min-width: 0;
    color: var(--neutral-900);
    font-size: var(--size-4);
    font-weight: 400;
    line-height: 1.625;
    overflow-wrap: anywhere;
}

.audit-agent-markdown :deep(> :first-child) {
    margin-top: 0;
}

.audit-agent-markdown :deep(> :last-child) {
    margin-bottom: 0;
}

.audit-agent-response :deep(h1),
.audit-agent-response :deep(h2),
.audit-agent-response :deep(h3),
.audit-agent-response :deep(h4) {
    margin: var(--space-5) 0 var(--space-2);
    color: var(--neutral-900);
    line-height: 1.25;
}

.audit-agent-response :deep(h1) {
    font-size: 1.375rem;
}

.audit-agent-response :deep(h2) {
    font-size: 1.25rem;
}

.audit-agent-response :deep(h3),
.audit-agent-response :deep(h4) {
    font-size: 1.0625rem;
}

.audit-agent-response :deep(p),
.audit-agent-response :deep(ul),
.audit-agent-response :deep(ol),
.audit-agent-response :deep(blockquote),
.audit-agent-response :deep(pre) {
    margin: 0 0 var(--space-3);
}

.audit-agent-response :deep(ul),
.audit-agent-response :deep(ol) {
    padding-left: 1.5rem;
}

.audit-agent-response :deep(li + li) {
    margin-top: var(--space-1);
}

.audit-agent-response :deep(blockquote) {
    padding-left: var(--space-3);
    border-left: 3px solid var(--primary-300);
    color: var(--neutral-700);
}

.audit-agent-response :deep(code) {
    padding: 0.125rem 0.25rem;
    border-radius: var(--radius-sm);
    background: var(--neutral-100);
}

.audit-agent-response :deep(table) {
    width: max-content;
    min-width: max(100%, 52rem);
    margin: 0;
    border: 1px solid var(--neutral-500);
    border-collapse: collapse;
    font-size: 0.875rem;
}

.audit-agent-response :deep(.audit-table-scroll) {
    max-width: 100%;
    margin: var(--space-3) 0 var(--space-4);
    overflow-x: auto;
    overflow-y: visible;
    border: 1px solid var(--neutral-500);
    border-radius: var(--radius-default);
    scrollbar-gutter: stable;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
}

.audit-agent-response :deep(.audit-table-scroll:focus-visible) {
    outline: 2px solid var(--primary-500);
    outline-offset: 2px;
}

.audit-agent-response :deep(th),
.audit-agent-response :deep(td) {
    min-width: 8rem;
    max-width: 24rem;
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--neutral-500);
    text-align: left;
    vertical-align: top;
    overflow-wrap: break-word;
    word-break: normal;
}

.audit-agent-response :deep(th:first-child),
.audit-agent-response :deep(td:first-child) {
    min-width: 10rem;
}

.audit-agent-response :deep(th:nth-child(n + 4)),
.audit-agent-response :deep(td:nth-child(n + 4)) {
    min-width: 16rem;
}

.audit-agent-response :deep(th) {
    background: var(--neutral-450);
    font-weight: 700;
}

.audit-agent-response :deep(tbody tr:nth-child(even)) {
    background: var(--neutral-450);
}

.audit-agent-response :deep(a) {
    color: var(--primary-700);
    text-decoration: underline;
}

.audit-agent-response :deep(.audit-inline-citation) {
    display: inline-flex;
    align-items: center;
    margin-left: 0.15rem;
    padding: 0.05rem 0.35rem;
    border: 1px solid var(--primary-200);
    border-radius: 999px;
    background: var(--primary-50);
    color: var(--primary-700);
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.25;
    vertical-align: super;
    cursor: pointer;
}

.audit-agent-response :deep(.audit-inline-citation:hover),
.audit-agent-response :deep(.audit-inline-citation:focus-visible) {
    border-color: var(--primary-500);
    background: var(--primary-100);
    outline: none;
}

@media (max-width: 575.98px) {
    .audit-agent-response :deep(table) {
        min-width: 48rem;
    }
}
</style>
